import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import ImmersiveLayout from '../components/ImmersiveLayout';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Heart, Plus, Eye, Edit3, Trash2, QrCode, Image as ImageIcon, Clock, Loader2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const EASE = [0.22, 1, 0.36, 1];

export default function MyMemoriesPage() {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const id = language === 'id';

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    axios.get(`${API}/memory-pages`, { withCredentials: true })
      .then(r => setPages(r.data.pages || []))
      .catch(() => setPages([]))
      .finally(() => setLoading(false));
  }, [user, navigate]);

  const handleDelete = async (pageId) => {
    if (!window.confirm(id ? 'Hapus memory page ini?' : 'Delete this memory page?')) return;
    try {
      await axios.delete(`${API}/memory-pages/${pageId}`, { withCredentials: true });
      setPages(prev => prev.filter(p => p.page_id !== pageId));
      toast.success(id ? 'Dihapus' : 'Deleted');
    } catch {
      toast.error(id ? 'Gagal menghapus' : 'Failed to delete');
    }
  };

  const drafts = pages.filter(p => p.status === 'draft');
  const published = pages.filter(p => p.status === 'published');

  return (
    <ImmersiveLayout>
      <section className="px-6 lg:px-16 pt-24 pb-32" data-testid="my-memories-page">
        <div className="max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }} className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <div className="flex items-center gap-3">
                <span className="w-8 h-px bg-seikat-pink" />
                <span className="eyebrow-immersive">{id ? 'Memorimu' : 'Your Memories'}</span>
              </div>
              <h1 className="mt-4 font-heading text-5xl lg:text-6xl text-seikat-bone leading-[0.95]">
                {id ? <>Jejak yang <em>diabadikan</em>.</> : <>Stories we've <em>preserved</em>.</>}
              </h1>
              <p className="mt-4 font-body text-seikat-bone/65 max-w-xl">
                {id ? 'Setiap memory page adalah surat tanpa pengirim yang bisa dibuka berulang kali.' : 'Each memory page is an unsigned letter, ready to be reopened any time.'}
              </p>
            </div>
            <Link to="/memory-builder/new">
              <button className="btn-pill btn-pill--solid" data-testid="create-new-memory-btn">
                <Plus size={14} /> {id ? 'Buat Baru' : 'Create New'}
              </button>
            </Link>
          </motion.div>

          {loading ? (
            <div className="mt-20 flex justify-center"><Loader2 size={24} className="animate-spin text-seikat-pink" /></div>
          ) : pages.length === 0 ? (
            <div className="mt-16 glass-dark rounded-2xl p-12 text-center" data-testid="memories-empty">
              <Heart size={36} className="mx-auto text-seikat-pink/60 mb-6" strokeWidth={1.4} />
              <h2 className="font-heading text-3xl text-seikat-bone">{id ? 'Belum ada memori' : 'No memories yet'}</h2>
              <p className="font-body text-seikat-bone/60 mt-3 max-w-md mx-auto">
                {id ? 'Mulai abadikan momen pertamamu.' : 'Begin preserving your first moment.'}
              </p>
              <div className="mt-8 flex flex-wrap gap-3 justify-center">
                <Link to="/memory-builder/new">
                  <button className="btn-pill btn-pill--solid" data-testid="create-first-memory-btn">
                    <Plus size={14} /> {id ? 'Memory Page' : 'Memory Page'}
                  </button>
                </Link>
                <Link to="/memory-bouquet">
                  <button className="btn-pill" data-testid="go-to-bouquet-btn">
                    <Heart size={14} /> {id ? 'Memory Bouquet' : 'Memory Bouquet'} <ArrowRight size={12} />
                  </button>
                </Link>
              </div>
            </div>
          ) : (
            <div className="mt-14 space-y-12">
              {drafts.length > 0 && (
                <Section title={id ? 'Draft' : 'Drafts'} icon={Clock} accent="amber" count={drafts.length}>
                  <Grid items={drafts} id={id} backend={BACKEND} onDelete={handleDelete} />
                </Section>
              )}
              {published.length > 0 && (
                <Section title={id ? 'Dipublikasikan' : 'Published'} icon={QrCode} accent="lime" count={published.length}>
                  <Grid items={published} id={id} backend={BACKEND} onDelete={handleDelete} />
                </Section>
              )}
            </div>
          )}
        </div>
      </section>
    </ImmersiveLayout>
  );
}

function Section({ title, icon: Icon, accent, count, children }) {
  const accentClass = accent === 'lime' ? 'text-seikat-lime' : 'text-amber-300';
  return (
    <div>
      <div className="flex items-center gap-2 mb-5">
        <Icon size={14} className={accentClass} />
        <h2 className="font-heading text-xl text-seikat-bone">{title}</h2>
        <span className="text-xs tracking-widest uppercase text-seikat-bone-muted">{count}</span>
      </div>
      {children}
    </div>
  );
}

function Grid({ items, id, backend, onDelete }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
      <AnimatePresence>
        {items.map((page, i) => {
          const firstAsset = page.assets?.[0];
          const imgSrc = firstAsset?.url ? `${backend}${firstAsset.url}` : null;
          const published = page.status === 'published';
          return (
            <motion.div
              key={page.page_id}
              layout
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.5, delay: i * 0.04, ease: EASE }}
              className="glass-dark rounded-2xl overflow-hidden group"
              data-testid={`memory-${page.page_id}`}
            >
              <div className="h-40 overflow-hidden bg-seikat-ink-2 relative">
                {imgSrc ? (
                  <img src={imgSrc} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Heart size={36} className="text-seikat-pink/30" strokeWidth={1.4} />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-seikat-ink via-transparent to-transparent" />
              </div>
              <div className="p-5">
                <div className="flex items-start justify-between mb-2 gap-2">
                  <h3 className="font-heading text-xl text-seikat-bone line-clamp-1">{page.title || 'Memory Page'}</h3>
                  <span className={`text-[10px] tracking-widest uppercase whitespace-nowrap px-2 py-0.5 rounded-full ${published ? 'bg-seikat-lime/15 text-seikat-lime' : 'bg-amber-400/10 text-amber-300'}`}>
                    {published ? (id ? 'Tayang' : 'Live') : 'Draft'}
                  </span>
                </div>
                <p className="font-body text-xs text-seikat-bone/55 line-clamp-2 mb-4">{page.message}</p>
                <div className="flex items-center gap-2 text-[10px] tracking-widest uppercase text-seikat-bone-muted mb-4">
                  <span className="px-2 py-0.5 rounded-full bg-white/5">{page.template}</span>
                  {page.assets?.length > 0 && <span className="flex items-center gap-1"><ImageIcon size={10} /> {page.assets.length}</span>}
                </div>
                <div className="flex gap-2">
                  {published ? (
                    <>
                      <Link to={`/memory/${page.page_id}`} className="flex-1">
                        <button className="btn-pill w-full justify-center text-[10px]" data-testid={`view-${page.page_id}`}>
                          <Eye size={12} /> {id ? 'Lihat' : 'View'}
                        </button>
                      </Link>
                      <Link to={`/memory-builder/${page.page_id}`}>
                        <button className="btn-pill px-3 text-[10px]" data-testid={`edit-${page.page_id}`}><Edit3 size={12} /></button>
                      </Link>
                    </>
                  ) : (
                    <Link to={`/memory-builder/${page.page_id}`} className="flex-1">
                      <button className="btn-pill w-full justify-center text-[10px]" data-testid={`resume-${page.page_id}`}>
                        <Edit3 size={12} /> {id ? 'Lanjutkan' : 'Resume'}
                      </button>
                    </Link>
                  )}
                  <button
                    className="btn-pill px-3 text-[10px] hover:border-red-400/50 hover:text-red-300"
                    onClick={() => onDelete(page.page_id)}
                    data-testid={`delete-${page.page_id}`}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
